import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';

import { Container, Left } from '@/components/container';
import OperationTabs, { OperationTabProps } from '@/components/operation-tabs';
import { useGlobalAudioPlayer } from '@/contexts/GlobalAudioPlayerContext';
import { useTranslate } from '@/hooks/useTranslate';
import { AIFunction } from '@/utils/const';
import { usePageVisitTrack } from '@/utils/eventlog/logAdapter';

import MyCreations from './_components/operation/my-creations';
import { OverviewPage, TemplateListPage } from './_components/operation/template-list';
import { useMyCreationsLoginGate } from './hooks/useMyCreationsLoginGate';
import {
  buildViralTemplateUgcOverviewQuery,
  listRouteCategoryUsesSubtabQuery,
  useViralTemplateRouteState,
  VIRAL_LIST_CATEGORY_ADS,
  VIRAL_LIST_CATEGORY_BY_TYPE_TAB,
  ViralTemplateCategory,
  ViralTemplateTab,
  ViralTemplateTabs,
  ViralTemplateTypeTab,
} from './hooks/useViralTemplateRouteState';

export { ViralTemplateTabs };

/** Tab 导航条之下内容区高度：视口减去页面整体留白与侧栏 Tab（可按设计微调） */
const VIRAL_OPERATION_TAB_CONTENT_HEIGHT_CLASS =
  '[&_.ant-tabs-nav]:!border-b-0 [&_.ant-tabs-nav:before]:!border-b-0 [&_.ant-tabs]:flex [&_.ant-tabs]:h-full [&_.ant-tabs]:min-h-0 [&_.ant-tabs]:flex-col [&_.ant-tabs-content-holder]:flex-1 [&_.ant-tabs-content-holder]:min-h-0 [&_.ant-tabs-content-holder]:overflow-hidden [&_.ant-tabs-content]:h-full [&_.ant-tabs-content]:min-h-0 [&_.ant-tabs-tabpane]:h-full [&_.ant-tabs-tabpane]:min-h-0';

const ViralTemplate = () => {
  const { t } = useTranslate();
  const { stop } = useGlobalAudioPlayer();
  const router = useRouter();
  const {
    activeMainTab,
    currentSubtab,
    currentCategory,
    handleMainTabChange,
    isRouterReady,
  } = useViralTemplateRouteState({
    onBeforeTabChange: stop,
  });
  const { isLoggedIn } = useMyCreationsLoginGate(activeMainTab);

  // 是否为大类预览页
  const isOverview = activeMainTab === ViralTemplateTab.LIST && !currentCategory;

  const goToCategory = useCallback(
    (category: ViralTemplateCategory, subtab?: ViralTemplateTypeTab) => {
      router.push({
        pathname: router.pathname,
        query: {
          tab: ViralTemplateTab.LIST,
          category,
          ...(listRouteCategoryUsesSubtabQuery(category)
            ? { subtab: subtab || ViralTemplateTypeTab.TRENDING }
            : {}),
        },
      });
    },
    [router],
  );

  const goBackToOverview = useCallback(() => {
    router.push({
      pathname: router.pathname,
      query: buildViralTemplateUgcOverviewQuery(
        currentCategory === ViralTemplateCategory.UGC ? currentSubtab : undefined,
      ),
    });
  }, [router, currentCategory, currentSubtab]);

  const CATEGORY_CONFIG = useMemo<
    Record<
      ViralTemplateCategory,
      {
        titleKey: string;
        descKey: string;
        showBack: boolean;
        fixedTab?: ViralTemplateTypeTab;
      }
    >
  >(
    () => ({
      [ViralTemplateCategory.UGC]: {
        titleKey: 'home.socialUgcAds',
        descKey: 'home.socialUgcAdsDesc',
        showBack: true,
      },
      [ViralTemplateCategory.META_TOP_ADS]: {
        titleKey: 'viralTemplate.metaTopAds.title',
        descKey: 'viralTemplate.metaTopAds.desc',
        showBack: false,
        fixedTab: ViralTemplateTypeTab.TRENDING,
      },
    }),
    [],
  );

  const renderListContent = () => {
    if (!isRouterReady) return null;

    if (isOverview) {
      return <OverviewPage onGoToCategory={goToCategory} />;
    }

    const category = currentCategory;
    if (!category || !CATEGORY_CONFIG[category]) {
      return <OverviewPage onGoToCategory={goToCategory} />;
    }

    const cfg = CATEGORY_CONFIG[category];

    const subtabName = ViralTemplateTabs.find(
      (tab) => tab.key === currentSubtab,
    )?.labelKey;
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
        <div className="flex items-center gap-[10px]">
          <div className="flex flex-col gap-[2px]">
            <span className="font-[500] text-[16px] leading-[22px] text-[#000]">
              {t(cfg.titleKey)}
            </span>
            {cfg.showBack && (
              <div
                className="flex items-center gap-[4px] cursor-pointer"
                onClick={goBackToOverview}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M14 16L10 12L14 8"
                    stroke="#111111"
                    strokeOpacity="0.8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <span className="text-[16px] text-[#111111CC]">
                  {subtabName && t(subtabName)}
                </span>
              </div>
            )}
          </div>
        </div>
        <TemplateListPage
          listCategory={
            category === ViralTemplateCategory.META_TOP_ADS
              ? VIRAL_LIST_CATEGORY_ADS
              : VIRAL_LIST_CATEGORY_BY_TYPE_TAB[
                  cfg.fixedTab ?? currentSubtab ?? ViralTemplateTypeTab.TRENDING
                ]
          }
        />
      </div>
    );
  };

  const items: OperationTabProps['items'] = [
    {
      key: ViralTemplateTab.LIST,
      label: (
        <div className="w-[105px] text-center">
          {t('eee83a9211a70f9dddd6bdac99f28511')}
        </div>
      ),
      children: renderListContent(),
    },
    {
      key: ViralTemplateTab.MY_CREATIONS,
      label: (
        <div className="w-[105px] text-center">
          {t('cfea33cd5cc402705358d2def1e7c902')}
        </div>
      ),
      children: (
        <MyCreations
          isLoggedIn={isLoggedIn}
          active={activeMainTab === ViralTemplateTab.MY_CREATIONS}
        />
      ),
    },
  ];

  usePageVisitTrack(AIFunction.ViralTemplate);

  return (
    <Container className="!p-0">
      <Left className="flex-1 px-[32px] py-[16px]">
        <OperationTabs
          className={`h-full ${VIRAL_OPERATION_TAB_CONTENT_HEIGHT_CLASS}`}
          justifyDirection="left"
          items={items}
          tabBarGutter={0}
          activeKey={activeMainTab}
          onChange={handleMainTabChange}
        />
      </Left>
    </Container>
  );
};

export default ViralTemplate;
